import { ThemeKey } from "@smartsheet/design-tokens";
import { CloseIcon as CloseIconLodestar } from "@smartsheet/lodestar-icons";
import { useActive } from "@smartsheet/lodestar-utils";
import { CloseIcon as CloseIconUrsa } from "@smartsheet/ursa-icons";
import React, { forwardRef } from "react";
import { useI18n } from "../../../../i18n/i18n";
import { useTheme } from "../../../../theme/utils/useTheme";
import { InputFieldBaseProps } from "../../../../util/inputFieldBaseProps";
import { useHovered } from "../../../../util/useHovered";
import { ResetFilteringReason } from "../../types";
import { ActionButton } from "./ActionButton";

interface SelectClearButtonSharedProps extends InputFieldBaseProps {
    /**
     * should focus either an input in a combobox, or a button in a select.
     */
    refocusTargetElement: () => void;

    /**
     * In a combobox, we need to clear out the input if when clear is pressed.
     */
    resetFiltering: (reason: ResetFilteringReason) => void;
}

type MultiSelectClearButtonProps = {
    clearSelectedItems: () => void;
    closeMenu: () => void;
    isMultiSelect: true;
    setActiveIndex: (newIndex: number) => void;
};

type BasicSelectClearButtonProps = {
    clearSelection: (value: null) => void; // replaces onChange
    isMultiSelect: false;
};
type CombinedButtonProps = MultiSelectClearButtonProps | BasicSelectClearButtonProps;
export type SelectClearButtonProps = SelectClearButtonSharedProps & CombinedButtonProps;

export const SelectClearButton = forwardRef<HTMLButtonElement, SelectClearButtonProps>(({ ...props }, ref) => {
    const { i18n } = useI18n();
    const { key } = useTheme();
    const themeMode = key as ThemeKey;
    const { isHovered, ...useHoveredHandlers } = useHovered<HTMLButtonElement>();
    const activeAndHoveredHandlers = useActive(useHoveredHandlers);

    return (
        <ActionButton
            {...activeAndHoveredHandlers}
            inputFieldHeight={props.inputFieldHeight}
            themeMode={themeMode}
            isHovered={isHovered}
            ref={ref}
            aria-label={i18n._(
                /*i18n*/ {
                    comment: "This deselects all the selected values",
                    id: "Clear selection",
                }
            )}
            onClick={(e) => {
                if (props.isMultiSelect) {
                    props.closeMenu?.();
                    // It's important that we call these two functions in this order.
                    // Why? IDK.
                    props.setActiveIndex?.(-1);
                    props.clearSelectedItems?.();
                } else {
                    props.clearSelection(null);
                }
                // For some reason, focusing this in the current tick causes the
                // setSelectedItems to do nothing.
                requestAnimationFrame(props.refocusTargetElement);
                // Don't let the container handle this.
                e.stopPropagation();
                props.resetFiltering?.("click-select-clear-button");
            }}
            className="sds-select-combobox-clear-button"
        >
            {(() => {
                if (themeMode.includes("ursa")) {
                    return <CloseIconUrsa size={16} />;
                } else {
                    return <CloseIconLodestar />;
                }
            })()}
        </ActionButton>
    );
});

SelectClearButton.displayName = "SelectClearButton";
